import api from './api.service';

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

/** Matches backend `RejectionPayloadInput` / universal reject bodies. */
export interface RejectionPayload {
  rejection_reason_code: string;
  remarks?: string;
}

export const MIN_REJECTION_REMARKS_LENGTH = 3;

export interface RejectionReasonDefinitionDTO {
  id: string;
  company_id: string;
  reason_code: string;
  name: string;
  category?: string | null;
  module_filter?: string[] | null;
  document_type_filter?: string[] | null;
  is_active: boolean;
  requires_additional_remarks: boolean;
  is_other_bucket: boolean;
  sort_order: number;
}

export interface RejectionLogDTO {
  id: string;
  company_id: string;
  module?: string | null;
  document_type: string;
  document_id: string;
  document_number?: string | null;
  action_type?: string | null;
  rejection_reason_code: string;
  rejection_reason_text?: string | null;
  remarks?: string | null;
  rejected_at: string;
  rejected_by_user_id?: string | null;
  previous_status?: string | null;
  new_status?: string | null;
  approval_instance_id?: string | null;
}

export interface RejectionRegisterResult {
  rows: RejectionLogDTO[];
  total: number;
}

export const rejectionService = {
  async listReasonsForDocument(documentType: string): Promise<RejectionReasonDefinitionDTO[]> {
    const q = new URLSearchParams({ document_type: documentType });
    const res = await api.get(`/rejection/reasons?${q.toString()}`);
    const raw = unwrap<RejectionReasonDefinitionDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  },

  async listLogsForDocument(documentType: string, documentId: string): Promise<RejectionLogDTO[]> {
    const q = new URLSearchParams({ document_type: documentType, document_id: documentId });
    const res = await api.get(`/rejection/logs/by-document?${q.toString()}`);
    const raw = unwrap<RejectionLogDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  },

  async listDefinitions(): Promise<RejectionReasonDefinitionDTO[]> {
    const res = await api.get('/rejection/definitions');
    const raw = unwrap<RejectionReasonDefinitionDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  },

  async createDefinition(body: {
    reason_code: string;
    name: string;
    category?: string | null;
    module_filter?: string[] | null;
    document_type_filter?: string[] | null;
    is_active?: boolean;
    requires_additional_remarks?: boolean;
    is_other_bucket?: boolean;
    sort_order?: number;
  }): Promise<RejectionReasonDefinitionDTO> {
    const res = await api.post('/rejection/definitions', body);
    return unwrap(res);
  },

  async updateDefinition(
    id: string,
    body: Partial<{
      name: string;
      category: string | null;
      module_filter: string[] | null;
      document_type_filter: string[] | null;
      is_active: boolean;
      requires_additional_remarks: boolean;
      is_other_bucket: boolean;
      sort_order: number;
    }>
  ): Promise<RejectionReasonDefinitionDTO> {
    const res = await api.put(`/rejection/definitions/${id}`, body);
    return unwrap(res);
  },

  async register(params: {
    from?: string;
    to?: string;
    document_type?: string;
    module?: string;
    reason_code?: string;
    rejected_by_user_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<RejectionRegisterResult> {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.document_type) q.set('document_type', params.document_type);
    if (params.module) q.set('module', params.module);
    if (params.reason_code) q.set('reason_code', params.reason_code);
    if (params.rejected_by_user_id) q.set('rejected_by_user_id', params.rejected_by_user_id);
    q.set('limit', String(params.limit ?? 50));
    q.set('offset', String(params.offset ?? 0));
    const res = await api.get(`/rejection/register?${q.toString()}`);
    const raw = unwrap<RejectionRegisterResult | RejectionLogDTO[]>(res);
    if (raw && typeof raw === 'object' && 'rows' in raw && 'total' in raw) {
      return raw as RejectionRegisterResult;
    }
    return { rows: Array.isArray(raw) ? raw : [], total: Array.isArray(raw) ? raw.length : 0 };
  },
};
